//
// Copyright (c) 2011-2020 by Curalate, Inc.
//
import { FeatureNames } from '@bvengineering/curalate-features';
import { ILocationService, ILogService, IWindowService } from 'angular';

import * as gatewayMediaApi from '@apis/gatewayMediaApi';
import ItemId from '@models/ItemId';
import { UniversalImageActionsView } from '@models/UniversalImageActionsView';
import { UniversalItemType } from '@models/UniversalItemType';
import { IUniversalImageActionsService } from '@react-bridge/wrappers/ng-service-provider-types';
import {
  IUniversalImageActionsHelpersService,
  IUrlUtilsService,
  UniversalItemId,
} from '@services/legacy-service-types';
import { SessionVarsService } from '@services/session-vars-service';
import { deetsIdRegex } from '@utils/stringUtils';

import UniversalImageActionsApiService from './universal-image-actions-api-service';

export class GlobalUIALauncher {
  constructor(
    private readonly $location: ILocationService,
    private readonly $log: ILogService,
    private readonly $window: IWindowService,
    private readonly sessionVarsService: SessionVarsService,
    private readonly UrlUtils: IUrlUtilsService,
    private readonly universalImageActionsApiService: UniversalImageActionsApiService,
    private readonly UniversalImageActionsHelpers: IUniversalImageActionsHelpersService,
    private readonly UniversalImageActionsService: IUniversalImageActionsService,
  ) {}

  public processNotesHash = () => {
    const hash = this.UrlUtils.getUrlParams(this.$window.location.search).uid;

    if (hash) {
      const params = {
        itemHash: hash,
      };

      this.universalImageActionsApiService
        .getUidForHashAsync(params)
        .then((response) => {
          if (response.itemId) {
            const item =
              this.UniversalImageActionsHelpers.parsePartialUniversalItemId(
                response.itemId,
                0,
              );

            this.UniversalImageActionsService.launch(
              UniversalImageActionsView.Notes.name,
              {
                itemId: item.itemId,
                itemType: item.itemType,
                autoPlayVideo: true, // no-op for non-video
              },
            );
          } else {
            this.$log.error(
              `No item ID returned from Notes permalink hash ${hash}`,
            );
          }
        })
        .catch((error: Error) => {
          this.$log.error(
            `Error when attempting to parse hash ${hash} from Notes permalink, ${error.message}`,
          );
        });
    }
  };

  public $onInit = async () => {
    const GLOBAL_ITEM_TYPES: UniversalItemType[] = [
      UniversalItemType.Facebook,
      UniversalItemType.Instagram,
      UniversalItemType.Pinterest,
      UniversalItemType.Syndication,
      UniversalItemType.Tumblr,
      UniversalItemType.Twitter,
    ];

    if (!this.sessionVarsService.isFeatureEnabled(FeatureNames.UiaPermalinks)) {
      this.processNotesHash();
      return;
    }

    let cuidOrDeetsId;
    let universalItemId: UniversalItemId = {} as UniversalItemId;
    let isDeetsItemId;

    try {
      cuidOrDeetsId = this.$location.search().openItem;
      if (cuidOrDeetsId) {
        if (cuidOrDeetsId.contains('.')) {
          universalItemId =
            this.UniversalImageActionsHelpers.parsePartialUniversalItemId(
              cuidOrDeetsId,
            );
        } else if (cuidOrDeetsId.match(deetsIdRegex)) {
          isDeetsItemId = true;
          // Fetch deets item from the web gateway with the given deets id
          const deetsItemResponse =
            await gatewayMediaApi.getDeetsItemFromId(cuidOrDeetsId);
          const deetsItem = deetsItemResponse.data[cuidOrDeetsId];
          if (deetsItem?.networkItems[0]) {
            universalItemId = {
              itemId: ItemId(deetsItem.networkItems[0].sourceId),
              itemType: deetsItem.networkItems[0].source,
              clientId: deetsItem.clientId.toString(),
            };
          }
        }
      }
    } catch (error) {
      if (isDeetsItemId) {
        this.$log.error(
          `Global UIA Launcher: Unable to process deets item id ${cuidOrDeetsId}`,
        );
      } else {
        this.$log.error(
          `Global UIA Launcher: Unable to process cuid ${cuidOrDeetsId}`,
        );
      }
      this.$log.error(error);
    }

    if (universalItemId.clientId) {
      if (
        universalItemId.clientId !== this.sessionVarsService.getClientId() &&
        !GLOBAL_ITEM_TYPES.includes(universalItemId.itemType)
      ) {
        this.$log.error(
          `Global UIA Launcher: Cannot open ${universalItemId.itemId}
           for client ${this.sessionVarsService.getClientId()}`,
        );
      } else {
        this.UniversalImageActionsService.launch(
          UniversalImageActionsView.Details.name,
          {
            itemId: ItemId(universalItemId.itemId),
            itemType: universalItemId.itemType,
            autoPlayVideo: false,
          },
        );
      }
    } else {
      this.processNotesHash();
    }
  };
}

angular.module('curalate').component('globalUiaLauncher', {
  controller: [
    '$location',
    '$log',
    '$window',
    'SessionVarsService',
    'UrlUtils',
    'UniversalImageActionsApiService',
    'UniversalImageActionsHelpers',
    'UniversalImageActionsService',
    GlobalUIALauncher,
  ],
});
